import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import ExcelWorkbook, { parseA1 } from './ExcelWorkbook';
import RunnerTopBar from './RunnerTopBar';
import ItemNavigator from './ItemNavigator';
import TaskBanner from './TaskBanner';
import McqView from './McqView';
import SubmitDialog from './SubmitDialog';
import useIntegrity from './useIntegrity';
import useCountdown from './useCountdown';
import { saveAssessment, saveAssessmentKeepalive, submitAssessment, apiError } from '../../services/assessmentApi';

const filled = (cell) => (cell?.values || []).some((row) => row.some((v) => v !== null && v !== undefined && String(v).trim() !== ''));

const area = (r) => (r.endRow - r.startRow + 1) * (r.endColumn - r.startColumn + 1);

// A rule counts for a task only when its ranges closely match the target (IoU >= 0.5)
const matches = (a1, ranges) => {
  const [a, b] = a1.split(':');
  const s = parseA1(a);
  const e = parseA1(b || a);
  const t = { startRow: s.r, endRow: e.r, startColumn: s.c, endColumn: e.c };
  let inter = 0;
  let total = 0;
  (ranges || []).forEach((r) => {
    total += area(r);
    const h = Math.min(r.endRow, t.endRow) - Math.max(r.startRow, t.startRow) + 1;
    const w = Math.min(r.endColumn, t.endColumn) - Math.max(r.startColumn, t.startColumn) + 1;
    if (h > 0 && w > 0) inter += h * w;
  });
  return inter > 0 && inter / (area(t) + total - inter) >= 0.5;
};

// Which spreadsheet tasks have something entered (drives the navigator ticks)
function sheetAnswered(items, collected) {
  const out = new Set();
  if (!collected) return out;
  items.forEach((it) => {
    if (it.kind === 'mcq') return;
    if (it.kind === 'cf' || it.kind === 'dv') {
      if ((collected.rules?.[it.sheet]?.[it.kind] || []).some((r) => matches(it.target, r.ranges))) out.add(it.id);
    } else if (filled(collected.cells?.[it.id])) out.add(it.id);
  });
  return out;
}

const isPermanent = (e) => { const s = e?.response?.status; return s >= 400 && s < 500 && s !== 408 && s !== 429; };
const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });

export const TestRunner = ({ attempt, onDone }) => {
  const { items, sheets } = attempt;
  const wbRef = useRef(null);
  const [mcq, setMcq] = useState(attempt.state?.mcq || {});
  const [activeId, setActiveId] = useState(items[0]?.id);
  const [sheetDone, setSheetDone] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dirty = useRef(false);
  const doneRef = useRef(false);
  const inflight = useRef(false);
  const saveRef = useRef(null);
  const payloadRef = useRef(null);
  const snapshot = useMemo(() => {
    try { return attempt.state?.snapshot_json ? JSON.parse(attempt.state.snapshot_json) : null; } catch { return null; }
  }, [attempt]);

  const onIntegrity = useCallback((type, secs) => {
    if (type === 'tab_switch') toast.warning(`You left the test window for ${secs}s — this is recorded in your report.`, { id: 'integrity' });
    else toast.error('Pasting content from outside the test is disabled and has been recorded.', { id: 'paste' });
    dirty.current = true;
    setTimeout(() => saveRef.current?.(), 1500);
  }, []);
  const [integrity] = useIntegrity({ active: true, onEvent: onIntegrity, initial: attempt.integrity });

  const payload = useCallback(() => {
    const c = wbRef.current?.collect() || {};
    return { mcq, cells: c.cells || {}, rules: c.rules || {}, snapshot_json: c.snapshot ? JSON.stringify(c.snapshot) : null, integrity, _c: c };
  }, [mcq, integrity]);
  payloadRef.current = payload;

  const finish = useCallback(async (auto) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setSubmitting(true);
    try {
      for (let i = 0; inflight.current && i < 50; i += 1) await wait(100); // eslint-disable-line no-await-in-loop
      const { _c, ...body } = payload();
      await submitAssessment(attempt.attempt_id, attempt.attempt_key, { ...body, auto });
      onDone();
    } catch (e) {
      doneRef.current = false;
      if (isPermanent(e)) toast.error(`${apiError(e, 'Submission failed')}. Please contact the hiring team — your saved answers are kept.`, { duration: 10000 });
      else {
        toast.error(apiError(e, 'Submission failed — retrying…'));
        setTimeout(() => finish(auto), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  }, [payload, attempt, onDone]);

  const left = useCountdown(attempt.deadline_at, attempt.server_now, () => {
    toast.info('Time is up — submitting your test.');
    finish(true);
  });

  useEffect(() => {
    if (left === 300) toast.warning('5 minutes left.', { id: 't5' });
    if (left === 60) toast.warning('1 minute left — your test will auto-submit.', { id: 't1' });
  }, [left]);

  const save = useCallback(async () => {
    if (!dirty.current || doneRef.current || inflight.current) return;
    dirty.current = false;
    inflight.current = true;
    setSaving(true);
    try {
      const { _c, ...body } = payload();
      setSheetDone(sheetAnswered(items, _c));
      const r = await saveAssessment(attempt.attempt_id, attempt.attempt_key, body);
      if (r.status !== 'in_progress') { doneRef.current = true; onDone(); }
    } catch {
      dirty.current = true;
    } finally {
      inflight.current = false;
      setSaving(false);
      if (dirty.current && !doneRef.current) setTimeout(() => saveRef.current?.(), 1500);
    }
  }, [payload, items, attempt, onDone]);
  saveRef.current = save;

  useEffect(() => { const id = setInterval(save, 15000); return () => clearInterval(id); }, [save]);
  useEffect(() => { dirty.current = true; const id = setTimeout(save, 800); return () => clearTimeout(id); }, [mcq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush unsaved work when the tab is hidden or the page is closed/reloaded
  useEffect(() => {
    const onVis = () => { if (document.hidden) saveRef.current?.(); };
    const onHide = () => {
      if (!dirty.current || doneRef.current) return;
      const { _c, ...body } = payloadRef.current();
      saveAssessmentKeepalive(attempt.attempt_id, attempt.attempt_key, body);
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', onHide); };
  }, [attempt]);

  const changeTimer = useRef(null);
  const onSheetChange = useCallback(() => {
    dirty.current = true;
    clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => saveRef.current?.(), 1500);
  }, []);

  // Restore ticks for work resumed from a saved snapshot
  useEffect(() => {
    const id = setTimeout(() => setSheetDone(sheetAnswered(items, wbRef.current?.collect())), 1500);
    return () => clearTimeout(id);
  }, [items]);

  const answered = useMemo(() => {
    const s = new Set(sheetDone);
    Object.entries(mcq).forEach(([k, v]) => { if (v !== null && v !== undefined) s.add(k); });
    return s;
  }, [sheetDone, mcq]);

  const idx = items.findIndex((i) => i.id === activeId);
  const item = items[idx];
  const select = (it) => {
    setActiveId(it.id);
    if (it.kind !== 'mcq') setTimeout(() => wbRef.current?.goTo(it.sheet, it.target), 30);
  };
  const go = (d) => { const next = items[Math.min(items.length - 1, Math.max(0, idx + d))]; if (next) select(next); };
  const mcqNum = items.filter((i) => i.kind === 'mcq').findIndex((i) => i.id === activeId) + 1;

  return (
    <div className="h-screen flex flex-col bg-slate-50 select-none" data-testid="test-runner">
      <RunnerTopBar name={attempt.candidate_name} answered={answered.size} total={items.length} left={left ?? attempt.duration_seconds}
        integrity={integrity} saving={saving} submitting={submitting} onSubmit={() => setConfirm(true)} />
      <div className="flex-1 flex min-h-0">
        <ItemNavigator items={items} activeId={activeId} answered={answered} onSelect={select} />
        <main className="flex-1 flex flex-col min-w-0 relative">
          {item && item.kind !== 'mcq' && (
            <TaskBanner item={item} index={idx + 1} onLocate={() => wbRef.current?.goTo(item.sheet, item.target)} onNext={() => go(1)} />
          )}
          <div className="flex-1 min-h-0 relative select-text">
            <ExcelWorkbook ref={wbRef} sheets={sheets} tasks={items.filter((i) => i.kind !== 'mcq')} snapshot={snapshot} onChange={onSheetChange} />
            {item?.kind === 'mcq' && (
              <McqView item={item} index={mcqNum} skillName={item.skill_name} value={mcq[item.id] ?? null}
                onChange={(v) => setMcq((m) => ({ ...m, [item.id]: v }))} onPrev={() => go(-1)} onNext={() => go(1)} />
            )}
          </div>
        </main>
      </div>
      <SubmitDialog open={confirm} onOpenChange={setConfirm} unanswered={items.length - answered.size} onConfirm={() => { setConfirm(false); finish(false); }} />
    </div>
  );
};

export default TestRunner;
